import { createContext, useContext, useLayoutEffect, useRef } from "react";

/** The board's roving Tab stop, handed to every card. */
export interface BoardRoving {
    /** The card holding focus, which windowed lists keep mounted. */
    focusedKey: string | null;
    /** The card holding the board's one Tab stop. */
    activeKey: string | null;
    /** The card an arrow key moved to: it takes focus once it's mounted. */
    focusTargetKey: string | null;
    /** The keyboard instructions for cards that don't open on Enter. */
    describedById: string;
    /** The same, plus Enter opening the card. */
    clickableDescribedById: string;
}

export const BoardRovingContext = createContext<BoardRoving | null>(null);

/** The placement key of the card a slot renders. */
export const BoardCardKeyContext = createContext<string | null>(null);

/**
 * A `BoardCard`'s part in the roving Tab stop: only the active card is
 * tabbable, and the card arrow keys moved to focuses itself as it mounts.
 * A windowed list can mount it commits after the key press (a lane coming
 * into view mounts its cards once it's measured), so the card waits for
 * itself instead of the board polling for it. On a board every card takes
 * part, clickable or not, so arrows and N reach all of them.
 */
export const useBoardCardRoving = () => {
    const roving = useContext(BoardRovingContext);
    const placementKey = useContext(BoardCardKeyContext);
    const nodeRef = useRef<HTMLDivElement | null>(null);
    const isBoardCard = roving !== null && placementKey !== null;
    const isFocusTarget = isBoardCard && roving.focusTargetKey === placementKey;
    useLayoutEffect(() => {
        const node = nodeRef.current;
        if (isFocusTarget && node !== null) {
            node.scrollIntoView({ block: "nearest", inline: "nearest" });
            node.focus({ preventScroll: true });
        }
    }, [isFocusTarget]);
    const getTabIndex = () => {
        if (!isBoardCard) {
            return undefined;
        }
        return roving.activeKey === placementKey ? 0 : -1;
    };
    return {
        nodeRef,
        /** 0 or -1 on a board; undefined outside one. */
        tabIndex: getTabIndex(),
        describedByIds: isBoardCard
            ? {
                  clickable: roving.clickableDescribedById,
                  plain: roving.describedById,
              }
            : undefined,
    };
};
